// FormBeneficiary.tsx
import React, { useEffect, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';

import { zodResolver } from '@hookform/resolvers/zod';
import { sub } from 'date-fns';

import { useLoginStore } from '../../stores/AuthStatusStore';
import FormBeneficiarylog from '../FormBeneficiarylog/log';
import LoginDefault from '../Login/newLogin';
import { DataTable } from './components/DataTable';
import { PlanoSelector } from './components/PlanoSelector';
import { SearchSection } from './components/SearchSection';
import { TipoSelecao } from './components/TipoSelecao';
import { useFormBeneficiary } from './hooks/useFormBeneficiary';
import { TitularFormSchema, titularSchema } from './schemas/beneficiarySchema';
import { useTableSelection } from './hooks/useTableSelection';
import { useSubmissionHandler } from './hooks/useSubmissionHandler';
import { UploadSection } from './components/UploadSection';

export default function FormBeneficiary(): ReactElement {
  const logado = useLoginStore(state => state.login);

  const {
    // Estados do formulário
    selecionado,
    setSelecionado,
    data,
    setData,
    departamentos,
    departamento,
    setDepartamento,
    vigencia,
    setVigencia,
    searchNome,
    setSearchNome,
    searchCpf,
    setSearchCpf,
    hasPlanoOne,
    hasPlanoNow,
    planoOne,
    setPlanoOne,
    planoNow,
    setPlanoNow,
    loading,
    setLoading,
    submissionResponse,
    setSubmissionResponse,

    // Funções
    handleFileChange,
    fetchDepartamentos,
  } = useFormBeneficiary();

  const {
    selectedRows,
    setSelectedRows,
    selectedDependentes,
    setSelectedDependentes,
    expandedRows,
    headerCheckboxRef,
    toggleSelect,
    toggleSelectDependente,
    toggleExpand,
    toggleAll,
    toggleAllDependentes,
  } = useTableSelection(data);

  const { handleSubmit } = useSubmissionHandler({
    data,
    selectedRows,
    selectedDependentes,
    selecionado,
    departamento,
    vigencia,
    setData,
    setSelectedRows,
    setSelectedDependentes,
    setSubmissionResponse,
    setLoading,
  });

  const {
    trigger,
    formState: { errors },
  } = useForm<TitularFormSchema>({
    resolver: zodResolver(titularSchema),
  });

  // Effects
  useEffect(() => {
    if (selecionado) {
      fetchDepartamentos();
    }
  }, [selecionado]);

  // Handlers
  const handleChangeDepartamento = (valor: any) => {
    if (valor) {
      setDepartamento(valor);
    }
  };

  const handleChangeOne = (key: keyof typeof planoOne) => {
    setPlanoOne(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleChangeNow = (key: keyof typeof planoNow) => {
    setPlanoNow(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleDeleteSelected = () => {
    setData(prev => prev.filter(titular => !selectedRows.includes(titular.id)));
    setSelectedRows([]);
    setSelectedDependentes({});
  };

  const handleDeleteSelectedDependentes = (titularId: string) => {
    const titular = data.find(t => t.id === titularId);
    if (!titular) return;

    const novosDependentes =
      titular.dependentes?.filter(
        dep => !selectedDependentes[titularId]?.includes(dep.id),
      ) || [];

    setData(prev =>
      prev.map(row =>
        row.id === titularId ? { ...row, dependentes: novosDependentes } : row,
      ),
    );

    setSelectedDependentes(prev => ({
      ...prev,
      [titularId]: [],
    }));
  };

  const handleClickEnviar = async () => {
    setLoading(true);

    try {
      if (!selecionado) {
        toast.error(
          'Por favor, selecione uma opção (Empresarial ou Administradora).',
        );
        return;
      }

      if (!vigencia) {
        toast.error('Por favor, informe a data de início de vigência.');
        return;
      }

      if (selectedRows.length === 0) {
        toast.error('Por favor, selecione pelo menos um titular para enviar.');
        return;
      }

      const submitData = {
        planoNow: hasPlanoNow ? planoNow : null,
        planoOne: hasPlanoOne ? planoOne : null,
      };

      await handleSubmit(submitData);
    } catch (error) {
      console.error('Erro no envio:', error);
      toast.error('Erro ao processar o envio.');
    } finally {
      setLoading(false);
    }
  };

  // Filtro de dados
  const filteredData = data.filter(item => {
    if (!searchNome && !searchCpf) return false;

    const nomeLower = searchNome?.toLowerCase() || '';
    const cpfSearch = searchCpf?.replace(/\D/g, '') || '';

    const matchTitularNome = searchNome
      ? item.nome?.toLowerCase().includes(nomeLower)
      : false;

    const matchTitularCpf = searchCpf ? item.cpf?.includes(cpfSearch) : false;

    const matchDependente = item.dependentes?.some(
      dep =>
        (searchNome && dep.nome?.toLowerCase().includes(nomeLower)) ||
        (searchCpf && dep.cpf?.replace(/\D/g, '').includes(cpfSearch)),
    );

    return matchTitularNome || matchTitularCpf || matchDependente;
  });

  const isNotFound = filteredData.length === 0 && (searchNome || searchCpf);

  /* if (!logado) {
    return (
      <div className="mx-auto mt-20 p-10 py-20 border rounded shadow">
        <h2 className="text-[2rem] font-semibold text-primary-main mt-12 text-center">
          Importação de excel Evo Saúde
        </h2>
        <LoginDefault />
      </div>
    );
  } */

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="mb-5 p-5">
        <div className="p-10">
          <form
            onSubmit={e => e.preventDefault()}
            className="max-w-6xl mx-auto p-8 mb-10 mt-10 rounded-xl shadow-lg grid gap-8"
          >
            <h2 className="text-3xl font-extrabold text-blue-900 text-center">
              Carregar Planilha Excel
            </h2>

            <TipoSelecao selecionado={selecionado} onChange={setSelecionado} />

            <UploadSection
              selecionado={selecionado}
              departamentos={departamentos}
              departamento={departamento}
              onDepartamentoChange={handleChangeDepartamento}
              vigencia={vigencia}
              onVigenciaChange={setVigencia}
              onFileChange={e => {
                const file = e.target.files?.[0];
                if (file) {
                  handleFileChange(file);
                }
              }}
            />

            <SearchSection
              searchNome={searchNome}
              searchCpf={searchCpf}
              onNomeChange={setSearchNome}
              onCpfChange={setSearchCpf}
              dataLength={data.length}
            />

            {isNotFound && (
              <div className="text-red-500 p-2">Nenhum registro encontrado</div>
            )}

            {data.length > 0 && (
              <DataTable
                data={searchNome || searchCpf ? filteredData : data}
                selectedRows={selectedRows}
                selectedDependentes={selectedDependentes}
                expandedRows={expandedRows}
                headerCheckboxRef={headerCheckboxRef}
                onToggleSelect={toggleSelect}
                onToggleSelectDependente={toggleSelectDependente}
                onToggleExpand={toggleExpand}
                onToggleAll={toggleAll}
                onToggleAllDependentes={toggleAllDependentes}
                onDeleteSelected={handleDeleteSelected}
                onDeleteSelectedDependentes={handleDeleteSelectedDependentes}
              />
            )}

            <PlanoSelector
              hasPlanoOne={hasPlanoOne}
              hasPlanoNow={hasPlanoNow}
              planoOne={planoOne}
              planoNow={planoNow}
              onChangeOne={handleChangeOne}
              onChangeNow={handleChangeNow}
            />
            {submissionResponse && (
              <FormBeneficiarylog formData={submissionResponse} />
            )}

            <div className="flex gap-4">
              <button
                type="button"
                onClick={handleClickEnviar}
                disabled={loading || selectedRows.length === 0}
                className={`w-1/2 py-4 font-extrabold rounded-xl shadow-lg transition ${
                  loading || selectedRows.length === 0
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-blue-900 hover:from-blue-700 hover:to-blue-800'
                } text-white`}
              >
                {loading ? 'Enviando...' : 'Enviar'}
              </button>
              <button
                type="button"
                onClick={() => (window.location.href = '/')}
                className="w-1/2 py-4 bg-gradient-to-r from-blue-600 to-blue-900 text-white font-extrabold rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition"
              >
                Sair
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
